/**
 * Utilitário para gerenciar a preferência "Permanecer logado" do usuário.
 * 
 * IMPORTANTE: O Supabase client usa SEMPRE localStorage para tokens.
 * NÃO tentamos migrar tokens entre storages pois isso quebra a autenticação.
 * A preferência "Permanecer logado" é usada apenas para controlar se a sessão
 * deve ser limpa ao fechar o navegador (via evento beforeunload).
 */

const REMEMBER_ME_KEY = 'autofluxo_remember_me';
const SUPABASE_AUTH_KEY = 'sb-mdckcgxuuyspxxnxuiuc-auth-token';

let beforeUnloadRegistered = false;

/**
 * Salva a preferência do usuário de "Permanecer logado"
 */
export const setRememberMePreference = (rememberMe: boolean) => {
  // Guarda 'true' ou 'false' explicitamente: a ausência da chave significa
  // "nunca escolheu" e o padrão é ficar logado (ver getRememberMePreference).
  localStorage.setItem(REMEMBER_ME_KEY, rememberMe ? 'true' : 'false');
};

/**
 * Verifica se o usuário optou por "Permanecer logado".
 * Padrão: ATIVO — só retorna false se o usuário desmarcou explicitamente.
 */
export const getRememberMePreference = (): boolean => {
  return localStorage.getItem(REMEMBER_ME_KEY) !== 'false';
};

/**
 * Limpa completamente todas as sessões de autenticação
 */
export const clearAllAuthSessions = () => {
  localStorage.removeItem(SUPABASE_AUTH_KEY);
  localStorage.removeItem(REMEMBER_ME_KEY);
  localStorage.removeItem('autofluxo_session'); // Legacy key
  sessionStorage.removeItem(SUPABASE_AUTH_KEY);
};

/**
 * Prepara o storage baseado na preferência do usuário ANTES do login
 * Apenas salva a preferência - NÃO move tokens entre storages
 */
export const prepareAuthStorage = (rememberMe: boolean) => {
  setRememberMePreference(rememberMe);
};

/**
 * Limpa apenas o token de autenticação do localStorage ao fechar o navegador.
 * Não remove a preferência remember_me.
 */
export const clearSessionOnClose = () => {
  localStorage.removeItem(SUPABASE_AUTH_KEY);
};

/**
 * Registra o listener global para limpar a sessão ao fechar o navegador,
 * se a preferência "Permanecer logado" não estiver ativa.
 */
export const registerGlobalAuthCleanup = () => {
  if (typeof window === 'undefined' || beforeUnloadRegistered) return;

  // IMPORTANTE (2026-07): O listener beforeunload foi DESATIVADO.
  //
  // Motivo: `beforeunload` dispara em qualquer saída da página — fechar aba,
  // recarregar, navegar via `window.location.replace`, etc. Não há como
  // distinguir esses cenários de forma confiável. Isso causava um bug
  // sério de "loop de login" para usuários que NÃO marcavam "Permanecer
  // logado": logo após o login bem-sucedido, o `window.location.replace('/inicio')`
  // disparava o cleanup, apagava o token `sb-*-auth-token` do localStorage
  // e o usuário voltava para /login. O mesmo problema afetava o
  // `hardReload()` do useAppVersionCheck.
  //
  // A preferência `remember_me` continua sendo salva para compatibilidade
  // com componentes existentes, mas não altera o comportamento de storage.
  // Para implementar verdadeiro "session-only", seria necessário trocar o
  // storage do supabase client para sessionStorage no boot (refatoração
  // maior, fora do escopo desta correção).
  beforeUnloadRegistered = true;
};
